import axios from "axios";
import { DateTime } from "luxon";
import { fetchObjects } from "@/services/firebase-helpers";

// For actual production deployment, you'll need:
// 1. A backend API endpoint that forwards requests to SendGrid
// 2. OR a serverless function (Firebase, AWS Lambda, etc.)
// The direct API calls from frontend won't work due to CORS restrictions

// Base URL for the API - adjust as needed for your environment
const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3001';

/**
 * Generate HTML email template for device alerts
 * @param {Array} alerts - The filtered alerts to include in the email
 * @param {Object} options - Configuration options for the email
 * @returns {String} The HTML email template
 */
export const generateAlertEmailHtml = (alerts, options = {}) => {
    const { onlyUnreadAlerts = true, baseUrl = window.location.origin } = options;

    // Get alert count
    const alertCount = alerts.length;

    // Get today's date for the email
    const today = DateTime.now().toFormat("MMMM d, yyyy");

    // Create HTML template
    let html = `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8">
    <style>
      body {
        font-family: Arial, sans-serif;
        line-height: 1.6;
        color: #333;
        max-width: 800px;
        margin: 0 auto;
      }
      .header {
        background-color: #000000;
        color: white;
        padding: 20px;
        text-align: center;
      }
      .logo {
        max-width: 200px;
        margin-bottom: 15px;
      }
      .content {
        padding: 20px;
      }
      .footer {
        background-color: #f5f5f5;
        padding: 10px 20px;
        text-align: center;
        font-size: 12px;
        color: #777;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 20px 0;
      }
      th, td {
        border: 1px solid #ddd;
        padding: 10px;
        text-align: left;
      }
      th {
        background-color: #f5f5f5;
      }
      .alert-link {
        color: #1976D2;
        text-decoration: underline;
      }
      .critical {
        background-color: rgba(244, 67, 54, 0.1);
      }
      .warning {
        background-color: rgba(255, 193, 7, 0.1);
      }
      .secure {
        background-color: rgba(76, 175, 80, 0.1);
      }
      .status-chip {
        padding: 4px 8px;
        border-radius: 16px;
        font-size: 12px;
        color: white;
        display: inline-block;
      }
      .status-critical {
        background-color: #F44336;
      }
      .status-warning {
        background-color: #FFC107;
        color: #333;
      }
      .status-secure {
        background-color: #4CAF50;
      }
    </style>
  </head>
  <body>
    <div class="header">
      <img src="https://framerusercontent.com/images/5aBkh59W5s6u90Rr6xx5K2BI.svg" alt="Wade-O-Lution Technologies" class="logo">
      <h1 style="color: white;">Device Alert Summary</h1>
      <p>${today}</p>
    </div>
    <div class="content">
      <h2>Alert Summary</h2>
      <p>You have ${alertCount} ${
      onlyUnreadAlerts ? "unread " : ""
    }device alerts that require your attention.</p>
  `;

    if (alertCount === 0) {
        html += `
      <p>No alerts match your current filter criteria.</p>
    `;
    } else {
        html += `
      <table>
        <thead>
          <tr>
            <th>Device ID</th>
            <th>Status</th>
            <th>Timestamp</th>
            <th>Deviation</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
    `;

        alerts.forEach((alert) => {
            const alertTimestamp = DateTime.fromISO(alert.timestamp).toFormat(
                "MMM d, HH:mm"
            );
            const statusClass = alert.status.toLowerCase();
            const alertUrl = `${baseUrl}/device/${alert.deviceId}/alerts?openAlert=${
        alert.id || alert.key
      }`;

            html += `
        <tr class="${statusClass}">
          <td>${alert.deviceId}</td>
          <td>
            <span class="status-chip status-${statusClass}">
              ${alert.status}
            </span>
          </td>
          <td>${alertTimestamp}</td>
          <td>${alert.torqueAngleDeviation}°</td>
          <td><a href="${alertUrl}" class="alert-link">View Alert</a></td>
        </tr>
      `;
        });

        html += `
        </tbody>
      </table>
    `;
    }

    html += `
      <p>Please review these alerts and take appropriate action.</p>
    </div>
    <div class="footer">
      <p>This is an automated email from the Integrity Critical Fastener Monitoring System. Please do not reply to this email.</p>
      <p>© ${new Date().getFullYear()} Wade-O-Lution Technologies</p>
    </div>
  </body>
  </html>
  `;

    return html;
};

/**
 * Fetch alerts from Firebase and filter them based on criteria
 * @param {Object} filterOptions - Options for filtering alerts
 * @returns {Array} Filtered and sorted alerts
 */
export const fetchAndFilterAlerts = async(filterOptions = {}) => {
    const {
        includeAlertTypes = { critical: true, warning: true, secure: false },
            onlyUnreadAlerts = true,
    } = filterOptions;

    try {
        // Fetch all alerts from Firebase
        const allAlerts = await fetchObjects("alerts");

        if (!allAlerts || allAlerts.length === 0) {
            console.log("No alerts found");
            return [];
        }

        // Filter alerts based on criteria
        const filteredAlerts = allAlerts.filter((alert) => {
            // Filter by alert status
            const statusMatch =
                (alert.status === "Critical" && includeAlertTypes.critical) ||
                (alert.status === "Warning" && includeAlertTypes.warning) ||
                (alert.status === "Secure" && includeAlertTypes.secure);

            // Filter by read status if onlyUnreadAlerts is true
            const readStatusMatch = onlyUnreadAlerts ? !alert.read : true;

            return statusMatch && readStatusMatch;
        });

        // Sort alerts by timestamp (newest first)
        return filteredAlerts.sort(
            (a, b) =>
            DateTime.fromISO(b.timestamp).toMillis() -
            DateTime.fromISO(a.timestamp).toMillis()
        );
    } catch (error) {
        console.error("Error fetching and filtering alerts:", error);
        throw error;
    }
};

/**
 * Send email using the backend API
 * 
 * @param {String} to - Recipient email address
 * @param {String} subject - Email subject
 * @param {String} htmlContent - Email HTML content
 * @param {String} from - Sender email address
 * @returns {Object} Response from the API
 */
export const sendEmail = async(
    to,
    subject,
    htmlContent,
    from = "alerts@wol-technologies.com"
) => {
    // Check environment - in development, we'll simulate sending if API isn't available
    const isDevelopment = process.env.NODE_ENV === 'development' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

    // Prepare email data
    const emailData = {
        to,
        subject,
        html: htmlContent,
        from
    };

    try {
        // Call the backend API to send the email
        const response = await axios.post(`${API_BASE_URL}/api/send-email`, emailData);

        console.log('Email sent successfully via API:', to);
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error sending email through API:', error);

        // In development, fall back to simulation if API call fails
        if (isDevelopment) {
            console.log('==== DEVELOPMENT MODE: SIMULATING EMAIL SEND (API FALLBACK) ====');
            console.log(`To: ${to}`);
            console.log(`Subject: ${subject}`);
            console.log(`From: ${from}`);
            console.log('HTML content preview:', htmlContent.substring(0, 150) + '...');

            return {
                success: true,
                data: { message: 'Email simulated successfully in development mode (API fallback)' },
                development: true,
                fallback: true
            };
        }

        return {
            success: false,
            error: (error.response && error.response.data) || error.message,
        };
    }
};

/**
 * Send alert email to a recipient
 * @param {String} recipientEmail - Email address to send to
 * @param {Object} options - Alert filtering options and email configuration
 * @returns {Object} Result of the send operation
 */
export const sendAlertEmail = async(recipientEmail, options = {}) => {
    try {
        // Fetch and filter alerts
        const filteredAlerts = await fetchAndFilterAlerts(options);

        // Generate email HTML
        const emailHtml = generateAlertEmailHtml(filteredAlerts, {
            onlyUnreadAlerts: options.onlyUnreadAlerts,
            baseUrl: options.baseUrl || window.location.origin,
        });

        // Prepare email data
        const emailData = {
            to: recipientEmail,
            subject: 'Device Alert Summary - Action Required',
            html: emailHtml,
        };

        try {
            // Try using the specialized alert endpoint first
            const response = await axios.post(`${API_BASE_URL}/api/send-alert-email`, emailData);

            return {
                success: true,
                data: response.data,
                alertCount: filteredAlerts.length,
            };
        } catch (specializedError) {
            console.warn('Specialized alert endpoint failed, falling back to general endpoint:', specializedError.message);

            // Fall back to the general email endpoint
            const result = await sendEmail(
                recipientEmail,
                'Device Alert Summary - Action Required',
                emailHtml
            );

            return {
                ...result,
                alertCount: filteredAlerts.length,
            };
        }
    } catch (error) {
        console.error("Error sending alert email:", error);
        return {
            success: false,
            error: error.message,
        };
    }
};

/**
 * PRODUCTION IMPLEMENTATION GUIDE
 * 
 * To make email sending work in production, you should:
 * 
 * 1. Create a backend API endpoint (Node.js, Express, etc.) that accepts:
 *    - recipient email
 *    - email subject
 *    - HTML content
 * 
 * 2. On your backend, use SendGrid's Node.js SDK to send the email:
 *    ```
 *    const sgMail = require('@sendgrid/mail');
 *    sgMail.setApiKey(process.env.SENDGRID_API_KEY);
 *    
 *    app.post('/api/send-email', async (req, res) => {
 *      try {
 *        const { to, subject, html, from = 'alerts@wol-technologies.com' } = req.body;
 *        
 *        const msg = {
 *          to,
 *          from,
 *          subject,
 *          html,
 *        };
 *        
 *        await sgMail.send(msg);
 *        res.status(200).json({ success: true });
 *      } catch (error) {
 *        console.error('Error sending email:', error);
 *        res.status(500).json({ success: false, error: error.message });
 *      }
 *    });
 *    ```
 * 
 * 3. Update this frontend service to call your backend API instead of SendGrid directly:
 *    ```
 *    const response = await axios.post('/api/send-email', {
 *      to: to,
 *      subject: subject,
 *      html: htmlContent,
 *      from: from
 *    });
 *    ```
 * 
 * 4. Alternative: Use Firebase Cloud Functions if you're already using Firebase:
 *    https://firebase.google.com/docs/functions/get-started
 */